import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { textFieldSx } from '@components/Checkout/steps/PhoneNumberStep/utils/phoneNumberStepUtils';
import { DirectoryListing } from '@components/Checkout/steps/PhoneNumberStep/DirectoryListing';
import { PhoneBillUpload } from '@components/Checkout/steps/PhoneNumberStep/PhoneBillUpload';
import type { PortInFieldsProps } from '@components/Checkout/steps/PhoneNumberStep/PhoneNumberStep.types';
import FormErrorMessage from '@shared/components/feedback/FormErrorMessage/FormErrorMessage';
import { StyledPhoneNumberInput } from '@shared/components/inputs/PhoneNumberInput/PhoneNumberInput.styles';
import SelectField from '@shared/components/inputs/SelectField/SelectField';
import TextFieldLabel from '@shared/components/inputs/TextFieldLabel/TextFieldLabel';

const OTHER_PROVIDER = { label: 'Other', value: 'Other' };

const NAME_ON_BILL_HINT =
  'The name on the bill for your current service provider must match with the name entered in the Customer section.';

const ACCOUNT_NUMBER_HINT =
  "Enter your current provider's account number. This can be found on your bill or in your account settings with your current provider.";

export const PortInFields = ({ formik, phoneProviders, directoryListings, onRemoveSavedFile }: PortInFieldsProps) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } = formik;

  const setField = (field: keyof typeof values, value: string) => {
    setFieldValue(field, value);
    setFieldTouched(field, true, false);
  };

  const providerOptions = [...phoneProviders.map((p) => ({ label: p.name, value: p.name })), OTHER_PROVIDER];
  const selectedProvider = providerOptions.find((o) => o.value === values.portInProvider) ?? null;

  const showPortInNumberError = touched.portInNumber && !!errors.portInNumber;
  const showProviderOtherError = touched.portInProviderOther && !!errors.portInProviderOther;
  const showNameOnBillError = touched.nameOnBill && !!errors.nameOnBill;
  const showAccountNumberError = touched.accountNumber && !!errors.accountNumber;

  return (
    <>
      <Stack gap="10px">
        <TextFieldLabel label="Enter Your Existing Phone Number" />
        <StyledPhoneNumberInput
          placeholder="(123) 456-7890"
          value={values.portInNumber}
          onChange={(value) => setField('portInNumber', value || '')}
          onBlur={() => setFieldTouched('portInNumber', true)}
          borderRadius="6px"
          height="41px"
          invalid={showPortInNumberError}
          defaultCountry="CA"
          fontSize="16px"
        />
        {showPortInNumberError && <FormErrorMessage>{errors.portInNumber}</FormErrorMessage>}
      </Stack>

      <Stack gap="10px">
        <TextFieldLabel label="Current Service Provider" />
        <SelectField
          placeholder="Select Provider"
          options={providerOptions}
          value={selectedProvider}
          onChange={(option) => {
            const opt = option as { value?: string } | null;
            setField('portInProvider', opt?.value ?? '');
          }}
          onBlur={() => setFieldTouched('portInProvider', true)}
          error={touched.portInProvider && !!errors.portInProvider}
          helperText={touched.portInProvider && (errors.portInProvider as string)}
        />
      </Stack>

      {values.portInProvider === 'Other' && (
        <Stack gap="10px">
          <TextFieldLabel label="Provider's Name" />
          <TextField
            sx={textFieldSx}
            placeholder="Enter provider's name..."
            value={values.portInProviderOther}
            onChange={(e) => setField('portInProviderOther', e.target.value)}
            onBlur={() => setFieldTouched('portInProviderOther', true)}
            error={showProviderOtherError}
            helperText={
              showProviderOtherError ? (
                <FormErrorMessage>{errors.portInProviderOther as string}</FormErrorMessage>
              ) : null
            }
          />
        </Stack>
      )}

      <Stack gap="10px">
        <Stack>
          <TextFieldLabel label="Name on Bill" />
          <Typography variant="body.sm" sx={{ lineHeight: '150%' }}>
            {NAME_ON_BILL_HINT}
          </Typography>
        </Stack>
        <TextField
          sx={textFieldSx}
          placeholder="Enter the name exactly how it appears on your bill with your current provider..."
          value={values.nameOnBill}
          onChange={(e) => setField('nameOnBill', e.target.value)}
          onBlur={() => setFieldTouched('nameOnBill', true)}
          error={showNameOnBillError}
          helperText={showNameOnBillError ? <FormErrorMessage>{errors.nameOnBill as string}</FormErrorMessage> : null}
        />
      </Stack>

      <Stack gap="10px">
        <Stack>
          <TextFieldLabel label="Account Number" />
          <Typography variant="body.sm" sx={{ lineHeight: '150%' }}>
            {ACCOUNT_NUMBER_HINT}
          </Typography>
        </Stack>
        <TextField
          sx={textFieldSx}
          placeholder="Enter account number..."
          value={values.accountNumber}
          onChange={(e) => setField('accountNumber', e.target.value)}
          onBlur={() => setFieldTouched('accountNumber', true)}
          error={showAccountNumberError}
          helperText={
            showAccountNumberError ? <FormErrorMessage>{errors.accountNumber as string}</FormErrorMessage> : null
          }
        />
      </Stack>

      <PhoneBillUpload
        files={values.phoneBillFiles}
        existingFiles={values.existingPhoneBillFiles}
        error={errors.phoneBillFiles as string | undefined}
        touched={touched.phoneBillFiles as boolean | undefined}
        onUpload={(file) => {
          setFieldValue('phoneBillFiles', [...values.phoneBillFiles, file]);
          setFieldTouched('phoneBillFiles', true, false);
        }}
        onRemoveLocal={(index) => {
          setFieldValue(
            'phoneBillFiles',
            values.phoneBillFiles.filter((_, i) => i !== index)
          );
        }}
        onRemoveExisting={onRemoveSavedFile}
      />

      <DirectoryListing
        value={values.directoryListing}
        options={directoryListings}
        headingVariant="roboto-600-16"
        onChange={(v) => setField('directoryListing', v)}
      />
    </>
  );
};
